import checkIcon from '@assets/icons/check.svg'
import { FC, useEffect, useRef, useState } from 'react'

import {
  Badge,
  StatusBadgeStyle,
  StyledCheckIcon,
  StyledMenuBox,
  StyledMenuItem,
  StyledMenuItemLabel,
  StyledRightdecoration,
} from './style'

type StatusBadgeProps = {
  isActive: boolean
  onStatusChange: (status: boolean) => Promise<void>
}

const STATUS_OPTIONS = [
  { value: true, label: 'Enabled' },
  { value: false, label: 'Disabled' },
] as const

const StatusBadge: FC<StatusBadgeProps> = ({ isActive, onStatusChange }) => {
  const [status, setStatus] = useState(isActive)
  const [isOpen, setIsOpen] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setStatus(isActive)
  }, [isActive])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggleDropdown = (event: React.MouseEvent) => {
    event.stopPropagation()
    if (!isUpdating) {
      setIsOpen((current) => !current)
    }
  }

  const handleStatusChange = async (event: React.MouseEvent, nextStatus: boolean) => {
    event.stopPropagation()

    if (isUpdating || nextStatus === status) {
      setIsOpen(false)
      return
    }

    setIsUpdating(true)
    setIsOpen(false)

    try {
      await onStatusChange(nextStatus)
      setStatus(nextStatus)
    } catch {
      // Error modal is handled by PlantCard
    } finally {
      setIsUpdating(false)
    }
  }

  const currentLabel = status ? 'Enabled' : 'Disabled'

  return (
    <StatusBadgeStyle ref={containerRef} onClick={(event) => event.stopPropagation()}>
      <Badge $isActive={status} onClick={handleToggleDropdown}>
        <span className='label'>{currentLabel}</span>
        <span className='chevron' />
      </Badge>
      {isOpen && (
        <StyledMenuBox>
          {STATUS_OPTIONS.map((option) => {
            const isSelected = status === option.value

            return (
              <StyledMenuItem
                key={option.label}
                $isSelected={isSelected}
                onClick={(event) => handleStatusChange(event, option.value)}
              >
                <StyledMenuItemLabel $isSelected={isSelected}>{option.label}</StyledMenuItemLabel>
                {isSelected && (
                  <StyledRightdecoration>
                    <StyledCheckIcon src={checkIcon} alt='' />
                  </StyledRightdecoration>
                )}
              </StyledMenuItem>
            )
          })}
        </StyledMenuBox>
      )}
    </StatusBadgeStyle>
  )
}

export default StatusBadge
